import { FC, useCallback, useState } from 'react';
import { Box, Button, Tab, Tabs, Typography, useTheme, useMediaQuery } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { titles } from '@utils/translations/en';
import { ScreenState } from '../index';
import ClientsTable from './ClientsTable';
import PvxTable from './PvxTable';
import PendingSignupsTable from './PendingSignupsTable';
import { UserManagementTableRow } from '../hooks/useUserManagementTable';
import { Search } from '@components';
import { ClientTableRow } from '../hooks/useClientsTable';

import {
  USER_MANAGEMENT_PAGE_NUMBER,
  UserManagementScreen,
  UserManagementTabs,
} from '../constants';
import { PvxPartnersTable } from './PvxPartnersTable';
import { PvxPartnerTableRow } from '../hooks/usePvxPartnersTable';
import { usePaginationState } from '@hooks/usePaginationState';

const actionButtonStyles = {
  textTransform: 'none',
  borderRadius: '100px',
  px: 3,
  py: 1,
  fontSize: '14px',
  fontWeight: '700',
  color: '#F6F8F9',
};

interface UserManagementListingProps {
  onNavigate: (screen: ScreenState) => void;
  selectedTab: UserManagementTabs;
  setSelectedTab: (tab: UserManagementTabs) => void;
}

const UserManagementListing: FC<UserManagementListingProps> = ({
  onNavigate,
  selectedTab,
  setSelectedTab,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { pagination, updatePagination, PaginationComponent } = usePaginationState();

  const resetPage = useCallback(
    () => updatePagination({ pageNumber: USER_MANAGEMENT_PAGE_NUMBER }),
    [],
  );
  const handleTotalChange = useCallback(
    (newTotal: number) => updatePagination({ total: newTotal }),
    [],
  );

  const handleSearchQuery = (value: string) => {
    setSearchQuery(value);
    updatePagination({ pageNumber: USER_MANAGEMENT_PAGE_NUMBER });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: UserManagementTabs) => {
    setSelectedTab(newValue);
    updatePagination({ pageNumber: USER_MANAGEMENT_PAGE_NUMBER, total: 0 });
  };

  const handleEditCompany = (row: ClientTableRow) => {
    onNavigate({ screen: UserManagementScreen.EDIT_COMPANY, company: row });
  };

  const handleEditPartner = (row: PvxPartnerTableRow) => {
    onNavigate({ screen: UserManagementScreen.EDIT_PVX_PARTNER, clientCode: row.id });
  };

  const handleEditPvxUser = (row: UserManagementTableRow) => {
    onNavigate({ screen: UserManagementScreen.EDIT_PVX_USER, userId: row.id });
  };

  const handleClone = (row: UserManagementTableRow) => {
    onNavigate({ screen: UserManagementScreen.CLONE_PVX_USER, userId: row.id });
  };

  const renderContent = () => {
    switch (selectedTab) {
      case UserManagementTabs.CLIENTS:
        return (
          <ClientsTable
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            searchQuery={searchQuery}
            onPageReset={resetPage}
            onTotalChange={handleTotalChange}
            onEditCompany={handleEditCompany}
          />
        );
      case UserManagementTabs.PARTNERS:
        return (
          <PvxPartnersTable
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            searchQuery={searchQuery}
            onPageReset={resetPage}
            onTotalChange={handleTotalChange}
            onEdit={handleEditPartner}
          />
        );
      case UserManagementTabs.PENDING:
        return (
          <PendingSignupsTable
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            searchQuery={searchQuery}
            onPageReset={resetPage}
            onTotalChange={handleTotalChange}
          />
        );
      case UserManagementTabs.ALL:
        return (
          <PvxTable
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            searchQuery={searchQuery}
            onPageReset={resetPage}
            onTotalChange={handleTotalChange}
            onEdit={handleEditPvxUser}
            onClone={handleClone}
          />
        );
      default:
        return null;
    }
  };

  const renderActionButtons = () => {
    switch (selectedTab) {
      case UserManagementTabs.ALL:
        return (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon htmlColor="#F6F8F9" />}
            sx={actionButtonStyles}
            onClick={() => onNavigate({ screen: UserManagementScreen.ADD_PVX_USER })}
          >
            New User
          </Button>
        );
      case UserManagementTabs.PARTNERS:
        return (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon htmlColor="#F6F8F9" />}
            sx={actionButtonStyles}
            onClick={() => onNavigate({ screen: UserManagementScreen.ADD_PVX_PARTNER })}
          >
            Add New Partner
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        height: '100%',
        border: 'none',
        padding: '1.5rem',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          marginTop: '0.75rem',
          marginBottom: '2rem',
          flexShrink: 0,
        }}
      >
        <Typography
          fontSize="1.5rem"
          fontWeight="600"
          lineHeight="1.5rem"
          fontFamily="Manrope,sans-serif"
          sx={{ color: '#1B1C17' }}
        >
          {titles.USERS}
        </Typography>
      </Box>

      <Box width={isMobile ? '100%' : '40%'} sx={{ marginBottom: '1rem', flexShrink: 0 }}>
        <Search
          search={search}
          setSearch={setSearch}
          placeholder="Search by Email / Role / Company"
          handleSearch={handleSearchQuery}
          styles={{
            backgroundColor: 'white',
          }}
        />
      </Box>
      <Box
        sx={{
          flex: '0 1 auto',
          maxHeight: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1rem',
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            aria-label="user management tabs"
            sx={{
              width: 'fit-content',
              borderBottom: '1px solid #E8EAEB',
              '& .MuiTabs-indicator': { backgroundColor: '#51B8B0' },
              '& .MuiTab-root': {
                fontSize: '0.875rem',
                fontWeight: 700,
                color: '#6C6C6C',
                lineHeight: '1.25rem',
                fontFamily: 'Manrope, sans-serif',
                textTransform: 'none',
                minWidth: 'auto',
                padding: '12px 42px',
                '&.Mui-selected': {
                  color: '#51B8B0',
                  borderBottom: '2px solid #51B8B0 !important',
                },
              },
            }}
          >
            <Tab disableRipple label={'Clients'} value={UserManagementTabs.CLIENTS} />
            <Tab disableRipple label={'Partners'} value={UserManagementTabs.PARTNERS} />
            <Tab disableRipple label={'Pending'} value={UserManagementTabs.PENDING} />
            <Tab disableRipple label={'All'} value={UserManagementTabs.ALL} />
          </Tabs>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            {renderActionButtons()}

            {pagination.total > 0 && <PaginationComponent />}
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
            '& .MuiTable-root': {
              minWidth: 'max-content',
            },
            '& .MuiTableContainer-root': {
              height: 'auto !important',
            },
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default UserManagementListing;
